import { useState } from 'react'
import { Lock } from 'lucide-react'
import { departamentulRolului, type DepartamentSlug } from '@b4l/shared'
import { useAuthStore } from '../../stores/auth'
import { AlegeDepartamente } from '../../components/taskuri/AlegeDepartamente'
import { ListaOameni, useOameni } from './AlegeOameni'
import { numeOameni, useUrmarescPrivatele } from './date'

/*
  Cui dai task-ul (Madalin, 19.10.2026): FIE departamentelor (il preia cine are timp), FIE unor
  responsabili (direct „În lucru" la ei). Privatul e o bifa la responsabili. Aceeasi alegere la
  „Task nou" si la „Convertește în task" din Inbox.
*/

export type ModDestinatie = 'departamente' | 'responsabili'

const eticheta = 'text-mic font-semibold text-b4l-text-muted'

/** Starea alegerii pe responsabili: oamenii, privatul, departamentul pe al carui tablou sta. */
export function useResponsabili(alMeu: DepartamentSlug | null, privatInitial = false) {
  const eu = useAuthStore((st) => st.user?.id)
  const [privat, setPrivat] = useState(privatInitial)
  /*
    Nimeni bifat din prima — nici cel care da task-ul (Victor, 20.10.2026: „elimina selectia
    automata a persoanei care atribuie"). Cine il vrea si pentru el, se bifeaza singur.
  */
  const [oameni, setOameni] = useState<number[]>([])
  const toti = useOameni()
  // Ceilalti oameni alesi (fara tine), cu nume — pentru buton si pentru explicatie.
  const altii = oameni
    .filter((id) => id !== eu)
    .map((id) => ({ name: toti.data?.users.find((u) => u.id === id)?.name ?? '' }))
  /*
    Un task pe responsabili, neprivat, sta si pe tabloul unui departament: implicit al primului
    om ales (sau al tau), schimbabil. Un admin n-are departament — atunci il alegi.
  */
  const [depAles, setDepAles] = useState<DepartamentSlug | null>(null)
  const primul = toti.data?.users.find((u) => u.id === oameni[0])
  const depImplicit = (primul ? departamentulRolului(primul.role) : null) ?? alMeu
  const departament = depAles ?? depImplicit
  /** Ce lipseste ca sa se poata trimite; `null` = gata. */
  const lipsa = !oameni.length
    ? 'Alege responsabilii'
    : !privat && !departament
      ? 'Alege departamentul'
      : null
  /** Eticheta butonului de trimitere. */
  const textButon =
    lipsa ??
    (!altii.length
      ? 'Creează, pentru tine'
      : oameni.includes(eu ?? -1)
        ? `Creează, pentru tine și ${numeOameni(altii)}`
        : `Dă-l lui ${numeOameni(altii)}`)
  return {
    eu,
    privat,
    setPrivat,
    oameni,
    setOameni,
    altii,
    departament,
    setDepAles,
    lipsa,
    textButon,
    /** Corpul cererii: `privat` + `oameni`, sau departamentul tabloului + `oameni`. */
    corp: () =>
      privat
        ? { privat: true as const, oameni }
        : { departamente: departament ? [departament] : [], oameni },
  }
}

export type StareResponsabili = ReturnType<typeof useResponsabili>

/** „Dai task-ul la": Departamente sau Responsabili. */
export function ComutatorDestinatie({
  mod,
  setMod,
}: {
  mod: ModDestinatie
  setMod: (m: ModDestinatie) => void
}) {
  return (
    <div className="grid gap-1.5">
      <span className={eticheta}>Dai task-ul la</span>
      <div className="grid grid-cols-2 gap-1.5" role="radiogroup" aria-label="Dai task-ul la">
        <button
          type="button"
          role="radio"
          aria-checked={mod === 'departamente'}
          onClick={() => setMod('departamente')}
          className={`grid rounded-caseta border px-3 py-2 text-left ${
            mod === 'departamente'
              ? 'border-b4l-blue bg-b4l-blue-light'
              : 'border-b4l-border bg-b4l-surface hover:border-b4l-blue'
          }`}
        >
          <b className="text-corp font-semibold text-b4l-text">Departamente</b>
          <span className="text-mic text-b4l-text-muted">îl preia cine are timp</span>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={mod === 'responsabili'}
          onClick={() => setMod('responsabili')}
          className={`grid rounded-caseta border px-3 py-2 text-left ${
            mod === 'responsabili'
              ? 'border-b4l-blue bg-b4l-blue-light'
              : 'border-b4l-border bg-b4l-surface hover:border-b4l-blue'
          }`}
        >
          <b className="text-corp font-semibold text-b4l-text">Responsabili</b>
          <span className="text-mic text-b4l-text-muted">direct la unul sau mai mulți colegi</span>
        </button>
      </div>
    </div>
  )
}

/** Oamenii, bifa „Privat" si departamentul pe al carui tablou apare. */
export function BlocResponsabili({
  r,
  alMeu,
}: {
  r: StareResponsabili
  alMeu: DepartamentSlug | null
}) {
  const urmaresc = useUrmarescPrivatele()
  // Fara cei deja numiti: tu si cei bifati.
  const urmarescAltii = urmaresc.numeFara([r.eu, ...r.oameni])
  return (
    <div className="grid gap-1.5">
      <span className={eticheta}>Responsabili</span>
      <ListaOameni
        alesi={r.oameni}
        setAlesi={r.setOameni}
        departament={r.departament}
        eticheta="Responsabili"
      />
      <label className="mt-1 inline-flex cursor-pointer items-start gap-2 text-corp text-b4l-text">
        <input
          type="checkbox"
          checked={r.privat}
          onChange={(e) => r.setPrivat(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-b4l-purple"
        />
        <span>
          <span className="inline-flex items-center gap-1 font-semibold text-b4l-purple-dark">
            <Lock size={13} aria-hidden="true" /> Privat
          </span>{' '}
          <span className="text-b4l-text-muted">
            — îl văd doar cei bifați și tu
            {urmarescAltii ? ` (și ${urmarescAltii}, care urmărește privatele)` : ''}
          </span>
        </span>
      </label>
      {r.privat ? (
        <p className="flex gap-2 rounded-caseta bg-b4l-purple-light px-3 py-2 text-mic text-b4l-purple-dark">
          <Lock size={14} className="mt-px shrink-0" aria-hidden="true" />
          <span>
            Departamentele și ceilalți admini nu-l văd. Intră direct „În lucru”; oricare dintre ei
            îl mută și îl închide.
            {r.altii.length > 0 && ' Fiecare primește o notificare și un email.'}
          </span>
        </p>
      ) : (
        <div className="grid gap-1 rounded-caseta bg-b4l-bg-soft px-3 py-2 text-mic text-b4l-text">
          <span>
            Intră direct „În lucru” la cei bifați; oricare dintre ei îl mută și îl închide.
            {r.altii.length > 0 && ' Fiecare primește o notificare și un email.'}
          </span>
          <span className="text-b4l-text-muted">Îl vede și departamentul, pe tablou:</span>
          <AlegeDepartamente
            alese={r.departament ? [r.departament] : []}
            setAlese={(d) => r.setDepAles(d[0] ?? null)}
            alMeu={alMeu}
            multe={false}
            eticheta="Departamentul în care apare"
          />
        </div>
      )}
    </div>
  )
}
